import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * LinkNav — route ordinary `<a href>` clicks through the router.
 *
 * The pages are verbatim ports of the EJS markup, so every link in them is a plain `<a href="/lists/3">`.
 * `BrowserRouter` only takes over navigation for `<Link>`, and nothing here is a `<Link>`: so every click asked
 * the browser for a new document, the server answered with index.html, React mounted again and every query
 * cache entry was thrown away. The result looks like a single-page app that reloads on every click — because,
 * for links, it was.
 *
 * Rewriting hundreds of anchors to `<Link>` would also rewrite the markup the parity contract pins. One
 * delegated listener does the same job and leaves the DOM alone:
 *
 *  - only plain left clicks, with no modifier keys and no `target`/`download` — the browser keeps everything else
 *    (middle click, ⌘-click, "open in new tab", CSV downloads);
 *  - only same-origin URLs, and only when they are not a bare in-page `#anchor`;
 *  - anything that is not a route (a file download, an API path) falls through to the browser.
 */
export function LinkNav() {
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a') as HTMLAnchorElement | null;
      if (!a) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download')) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Downloads and API paths are the server's, not the router's: a CSV link must stay a download.
      if (/\.(csv|xlsx|pdf|zip|png|jpe?g|svg|ico)$/i.test(url.pathname) || url.pathname.startsWith('/api/')) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      e.preventDefault();
      navigate(url.pathname + url.search + url.hash);
      // A new document starts at the top; a client-side route change has to say so.
      window.scrollTo(0, 0);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);

  return null;
}
